import { useEffect, useState } from 'react'

import { useProgressAnimation } from 'hooks/useProgressAnimation'

import { linear } from 'helpers/animationFunctions'

import { LABEL_FADE_MS } from './constants'

type TLoadingStage = {
  minProgress: number
  text: string
}

type TLoadingStagesParams = {
  duration: number
  initialLabelKey: string
  stages: readonly TLoadingStage[]
  onFinish: () => void
}

export const useLoadingStages = ({
  duration,
  initialLabelKey,
  stages,
  onFinish,
}: TLoadingStagesParams) => {
  const [shownLabelKey, setShownLabelKey] = useState(initialLabelKey)

  const { counter, progressAnimationPlayback } = useProgressAnimation({
    duration,
    animationFunction: linear,
    callback: onFinish,
  })

  const stageLabelKey =
    stages.find(({ minProgress }) => counter >= minProgress)?.text ??
    initialLabelKey
  const isLabelChanging = shownLabelKey !== stageLabelKey

  useEffect(() => {
    progressAnimationPlayback()
  }, [progressAnimationPlayback])

  useEffect(() => {
    if (!isLabelChanging) return undefined

    const labelSwapTimer = setTimeout(
      () => setShownLabelKey(stageLabelKey),
      LABEL_FADE_MS,
    )

    return () => clearTimeout(labelSwapTimer)
  }, [isLabelChanging, stageLabelKey])

  return {
    counter,
    labelKey: shownLabelKey,
    isLabelHidden: isLabelChanging,
  }
}
